import * as React from "react";
import { Box, Button } from "@mui/material";
import DatePicker from "components/Common/DatePicker/DatePicker";
import { saveEmployeeDateOverrides } from "services/centerPointUserServices";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import PropTypes from "prop-types";
import classes from "./EmployeeDateOverrides.module.css";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";

const validateDates = (training, nesting, production) => {
  const errors = { trainingDate: "", nestingDate: "", productionDate: "" };
  const anyFilled = training || nesting || production;

  if (anyFilled) {
    if (!training)
      errors.trainingDate = "Training Date is required when any date is set.";
    if (!production)
      errors.productionDate =
        "Production Start Date is required when any date is set.";
  }

  if (training && production) {
    const t = new Date(training);
    const p = new Date(production);
    if (nesting) {
      const n = new Date(nesting);
      if (n < t)
        errors.nestingDate = "Nesting Date must be on or after Training Date.";
      if (p < n)
        errors.productionDate =
          "Production Start Date must be on or after Nesting Date.";
    } else {
      if (p < t)
        errors.productionDate =
          "Production Start Date must be on or after Training Date.";
    }
  }

  return errors;
};

const hasErrors = (errors) => Object.values(errors).some((msg) => msg !== "");

const EmployeeDateOverrides = ({ cpEmployeeId, initialDates }) => {
  const savedRef = React.useRef(initialDates);

  const [dates, setDates] = React.useState(initialDates);
  const [errors, setErrors] = React.useState({
    trainingDate: "",
    nestingDate: "",
    productionDate: "",
  });
  const [loader, setLoader] = React.useState(false);
  const [snack, setSnack] = React.useState({
    open: false,
    message: "",
    severity: "",
  });

  React.useEffect(() => {
    savedRef.current = initialDates;
    setDates(initialDates);
    setErrors({ trainingDate: "", nestingDate: "", productionDate: "" });
  }, [initialDates]);

  const handleChange = (field, val) => {
    setDates((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleRevert = () => {
    setDates(savedRef.current);
    setErrors({ trainingDate: "", nestingDate: "", productionDate: "" });
  };

  const handleSave = async () => {
    const validationErrors = validateDates(
      dates.trainingDate,
      dates.nestingDate,
      dates.productionDate,
    );
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }
    setLoader(true);
    try {
      const payload = {
        cpEmployeeId,
        trainingDateOverride: dates.trainingDate || null,
        nestingDateOverride: dates.nestingDate || null,
        productionStartDateOverride: dates.productionDate || null,
        loggedOnUsername: localStorage.getItem("User"),
      };
      const response = await saveEmployeeDateOverrides(payload);
      if (response?.data?.message === "Success") {
        savedRef.current = { ...dates };
        setSnack({
          open: true,
          message: "Date overrides saved successfully.",
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: "Failed to save date overrides. Please try again.",
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: "Something went wrong while saving.",
        severity: "error",
      });
    }
    setLoader(false);
  };

  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  return (
    <>
      {loader && <BackdropComponent />}

      {/* Inline layout: date pickers + buttons sit naturally beside the name */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <DatePicker
          label="Training Date Override"
          value={dates.trainingDate}
          onChange={(val) => handleChange("trainingDate", val)}
          error={!!errors.trainingDate}
          helperText={errors.trainingDate}
        />
        <DatePicker
          label="Nesting Date Override"
          value={dates.nestingDate}
          onChange={(val) => handleChange("nestingDate", val)}
          error={!!errors.nestingDate}
          helperText={errors.nestingDate}
        />
        <DatePicker
          label="Production Start Date Override"
          value={dates.productionDate}
          onChange={(val) => handleChange("productionDate", val)}
          error={!!errors.productionDate}
          helperText={errors.productionDate}
        />

        <Box className={classes.actionButtons}>
          <CustomButton
            variant="outlined"
            onClick={handleRevert}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              fontFamily: "urbanist",
            }}
          >
            Revert
          </CustomButton>
          <CustomButton
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              fontFamily: "urbanist",
            }}
          >
            Save
          </CustomButton>
        </Box>
      </Box>

      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
};

EmployeeDateOverrides.propTypes = {
  cpEmployeeId: PropTypes.number.isRequired,
  initialDates: PropTypes.shape({
    trainingDate: PropTypes.string,
    nestingDate: PropTypes.string,
    productionDate: PropTypes.string,
  }).isRequired,
  errorHandler: PropTypes.func,
};

export default EmployeeDateOverrides;
